'use client';
import React, { useEffect, useState } from 'react';
import styles from './FeaturedContent.module.css';
import { PDFViewer, PdfPicker } from './../pdfViewer';
import type { PdfPickerProps } from './../pdfViewer';

export interface FeaturedPdf {
  pdfPicker?: PdfPickerProps;
  pdfs?: string[];
  pdfViewer?:
    | { rootPath?: string; rootString?: string | null; searchQuery?: string }
    | boolean;
}

export interface FeaturedPage {
  title: string;
  url: string;
  section: string;
  // 'page'  → embed the page itself in an iframe (bare ?embed=1 layout)
  // 'video' → standardized player: <video> for a direct file (mediaUrl),
  //           else an iframe for an embed URL (embedUrl, e.g. YouTube)
  // 'pdf'   → standardized viewer: PdfPicker/PDFViewer (pdf), else an iframe of
  //           the site's PDF gallery page (embedUrl)
  kind: 'page' | 'video' | 'pdf';
  embedUrl?: string;   // page: /sec/slug?embed=1 · video: embed src · pdf: gallery url
  mediaUrl?: string;   // video: direct .mp4/.webm file → <video>
  poster?: string;     // video/pdf: thumbnail image
  pdf?: FeaturedPdf;
}

// Renders the selected item's body by kind. Pages are shown as an iframe of the
// page (chrome-stripped via ?embed=1) rather than dumped inline; video/PDF get a
// consistent, standardized component instead of a raw iframe.
const FeaturedBody: React.FC<{ item: FeaturedPage }> = ({ item }) => {
  if (item.kind === 'video') {
    // Direct media file (the /videos hub serves local .mp4s) → native player.
    if (item.mediaUrl) {
      return (
        <div className={styles.videoWrap}>
          <video
            className={styles.videoFrame}
            src={item.mediaUrl}
            poster={item.poster}
            controls
            preload="metadata"
            playsInline
          />
        </div>
      );
    }
    // Embed URL (e.g. a YouTube/Vimeo page media entry) → iframe player.
    if (item.embedUrl) {
      return (
        <div className={styles.videoWrap}>
          <iframe
            className={styles.videoFrame}
            src={item.embedUrl}
            title={item.title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            loading="lazy"
          />
        </div>
      );
    }
  }

  if (item.kind === 'pdf') {
    // Page-declared PDFs → the site's PdfPicker/PDFViewer components.
    if (item.pdf) {
      const { pdfPicker, pdfs, pdfViewer } = item.pdf;
      if (pdfPicker?.items?.length) {
        return <PdfPicker {...pdfPicker} />;
      }
      if (pdfs?.length) {
        return (
          <div className={styles.pdfStack}>
            {pdfs.map((baseId) => (
              <div key={baseId} className={styles.pdfContainer}>
                <PDFViewer baseId={baseId} rootPath="pdfs" />
              </div>
            ))}
          </div>
        );
      }
      if (pdfViewer && typeof pdfViewer === 'object') {
        return (
          <div className={styles.pdfContainer}>
            <PDFViewer
              rootPath={pdfViewer.rootPath ?? 'pdfs'}
              rootString={pdfViewer.rootString ?? null}
              searchQuery={pdfViewer.searchQuery ?? ''}
            />
          </div>
        );
      }
    }
    // Hub PDFs → iframe the site's standardized /pdfs gallery viewer.
    if (item.embedUrl) {
      return (
        <div className={styles.pageFrameWrap}>
          <iframe className={styles.pageFrame} src={item.embedUrl} title={item.title} loading="lazy" />
        </div>
      );
    }
  }

  // Default: embed the page itself. embedUrl carries ?embed=1 so the iframe
  // shows just the page content, not the nested site shell.
  return (
    <div className={styles.pageFrameWrap}>
      <iframe
        className={styles.pageFrame}
        src={item.embedUrl || item.url}
        title={item.title}
        loading="lazy"
      />
    </div>
  );
};

// Homepage feature: surfaces a real page from across the site. Picks at random
// on mount; Shuffle re-picks.
export const FeaturedContent: React.FC<{ pages: FeaturedPage[]; heading?: string }> = ({
  pages,
  heading = 'From across the site',
}) => {
  const [idx, setIdx] = useState<number | null>(pages && pages.length ? 0 : null);
  useEffect(() => {
    if (pages && pages.length > 1) setIdx(Math.floor(Math.random() * pages.length));
  }, [pages]);

  if (!pages || pages.length === 0 || idx === null) return null;
  const p = pages[idx];
  const shuffle = () => {
    if (pages.length < 2) return;
    let n = idx;
    while (n === idx) n = Math.floor(Math.random() * pages.length);
    setIdx(n);
  };

  const kindLabel = p.kind === 'video' ? '▶ video' : p.kind === 'pdf' ? 'PDF' : 'page';

  return (
    <section className={styles.featured} aria-label={heading}>
      <div className={styles.head}>
        <div className={styles.headText}>
          <span className={styles.eyebrow}>{heading}</span>
          <a className={styles.pageLink} href={p.url}>{p.title} →</a>
          <span className={styles.metaRow}>
            <span className={`${styles.badge} ${styles[p.kind]}`}>{kindLabel}</span>
            {p.section ? <span className={styles.section}>in {p.section}</span> : null}
          </span>
        </div>
        <button type="button" className={styles.shuffle} onClick={shuffle}>↻ Shuffle</button>
      </div>
      <FeaturedBody item={p} />
      <div className={styles.footLink}><a href={p.url}>Open the full page: {p.title} →</a></div>
    </section>
  );
};

export default FeaturedContent;
